import { ArrowUpRight, Briefcase, Workflow, Factory, Code2, Layers, Handshake } from "lucide-react";
import { Reveal, SectionTag } from "./shared";
import { useLanguage } from "@/i18n/LanguageContext";

const SERVICES_IMG =
    "https://images.unsplash.com/photo-1714974528693-f77f6fcc56af?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwxfHxhZHZpc29yeSUyMG1lZXRpbmclMjBkYXJrJTIwbHV4dXJ5fGVufDB8fHx8MTc4NzkzMTM1NXww&q=85&w=1200";

const ICONS = {
    consultancy: Briefcase,
    optimisation: Workflow,
    platform: Factory,
    custom: Code2,
    integration: Layers,
    matchmaking: Handshake,
};

const SPANS = {
    consultancy: "md:col-span-7",
    optimisation: "md:col-span-4",
    platform: "md:col-span-4",
    custom: "md:col-span-4",
    integration: "md:col-span-5",
    matchmaking: "md:col-span-7",
};

const Tile = ({ icon: Icon, title, body, className = "", testid, delay = 0 }) => (
    <Reveal className={className} delay={delay}>
        <div
            data-testid={testid}
            className="group relative h-full overflow-hidden border border-white/10 bg-coal p-8 transition-colors duration-500 hover:border-gold/40 md:p-10"
        >
            <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-gold/0 blur-3xl transition-colors duration-700 group-hover:bg-gold/10" />
            <div className="flex items-start justify-between">
                <Icon className="h-6 w-6 text-gold" strokeWidth={1.5} />
                <ArrowUpRight className="h-5 w-5 text-ash opacity-0 transition-all duration-500 group-hover:translate-x-1 group-hover:text-gold group-hover:opacity-100" />
            </div>
            <h3 className="mt-14 font-display text-2xl tracking-tight text-bone md:mt-16 md:text-3xl">
                {title}
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-ash">{body}</p>
        </div>
    </Reveal>
);

export const Services = () => {
    const { t } = useLanguage();
    const tiles = Object.fromEntries(t.services.tiles.map((tile) => [tile.id, tile]));
    const order = ["consultancy", null, "optimisation", "matchmaking", "custom", "integration", "platform"];

    return (
        <section id="services" data-testid="services-section" className="relative py-24 md:py-32">
            <div className="mx-auto max-w-[1400px] px-6 md:px-12">
                <div className="flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
                    <div>
                        <Reveal>
                            <SectionTag index="№ 02" label={t.services.tag} />
                        </Reveal>
                        <Reveal delay={0.1}>
                            <h2 className="mt-8 font-display text-4xl leading-[1.05] tracking-tight md:text-5xl">
                                {t.services.headingA}{" "}
                                <span className="italic text-gold">{t.services.headingB}</span>
                            </h2>
                        </Reveal>
                    </div>
                    <Reveal delay={0.2}>
                        <p className="max-w-sm text-base leading-relaxed text-ash">
                            {t.services.intro}
                        </p>
                    </Reveal>
                </div>

                <div className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-12">
                    {order.map((id, i) => {
                        if (id === null) {
                            return (
                                <Reveal key="image" className="md:col-span-5" delay={0.1}>
                                    <div
                                        data-testid="services-image-tile"
                                        className="group relative h-full min-h-[320px] overflow-hidden border border-white/10"
                                    >
                                        <img
                                            src={SERVICES_IMG}
                                            alt="Automation software dashboard with live graphs"
                                            className="animate-kenburns absolute inset-0 h-full w-full object-cover"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                                        <p className="absolute bottom-6 left-6 font-display text-xl italic text-bone">
                                            {t.services.imageCaption}
                                        </p>
                                    </div>
                                </Reveal>
                            );
                        }
                        const tile = tiles[id];
                        return (
                            <Tile
                                key={id}
                                testid={`service-tile-${id}`}
                                className={SPANS[id]}
                                icon={ICONS[id]}
                                title={tile.title}
                                body={tile.body}
                                delay={(i % 3) * 0.05}
                            />
                        );
                    })}
                </div>
            </div>
        </section>
    );
};
